import { Fragment, useEffect, useState } from 'react'
import { Listbox, Transition } from '@headlessui/react'
import { HiCheckCircle, HiSelector } from 'react-icons/hi'
import type { Account } from 'wallet/index'
import { classNames } from 'src/helpers/utilities'

type AccountsListProps = {
  accounts: Account[]
  activeAccount: Account
  handleSetActiveAccount: (account: Account) => void
}

export default function AccountsList({
  accounts,
  activeAccount,
  handleSetActiveAccount
}: AccountsListProps) {
  const [selected, setSelected] = useState<Account | null>(null)
  const activeAccountDependency = JSON.stringify(activeAccount)

  useEffect(() => {
    if (
      activeAccount &&
      accounts.some((account) => account.providerId === activeAccount.providerId)
    ) {
      setSelected(activeAccount)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAccountDependency])

  const handleSelectActiveAccount = (account: Account) => {
    setSelected(account)
    handleSetActiveAccount(account)
  }

  return (
    <Listbox value={selected} onChange={handleSelectActiveAccount}>
      <div className="relative flex-1">
        <Listbox.Button className="relative w-full py-2 pl-3 pr-10 text-left border border-gray-300 shadow-sm bg-white rounded-md cursor-default focus:outline-none focus-visible:ring-2 focus-visible:ring-opacity-75 focus-visible:ring-white focus-visible:ring-offset-orange-300 focus-visible:ring-offset-2 focus-visible:border-blue-500 sm:text-sm">
          <span className="flex items-center text-sm text-gray-500">
            {/* <HiCash className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" aria-hidden="true" /> */}
            <span className="block truncate">{selected?.name}</span>
            <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
              <HiSelector className="w-5 h-5 text-gray-400" aria-hidden="true" />
            </span>
          </span>
        </Listbox.Button>
        <Transition
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Listbox.Options className="absolute z-10 w-full py-1 mt-1 overflow-auto text-base bg-white rounded-md shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
            {accounts.map((account) => (
              <Listbox.Option
                key={account.address}
                className={({ active }) =>
                  classNames(
                    active ? 'text-amber-900 bg-gray-100' : 'text-gray-900',
                    'cursor-default select-none relative py-2 pl-10 pr-4'
                  )
                }
                value={account}
              >
                {({ selected }) => (
                  <span className="flex items-center text-sm text-gray-500">
                    <span className="block truncate">
                      <span className={selected ? 'font-medium text-gray-700' : 'font-normal'}>
                        {account.name}:{' '}
                      </span>
                      <span className="text-gray-400 font-mono">{account.address}</span>
                    </span>
                    {selected ? (
                      <span className="text-brand-500 absolute inset-y-0 left-0 flex items-center pl-3">
                        <HiCheckCircle className="w-5 h-5" aria-hidden="true" />
                      </span>
                    ) : null}
                  </span>
                )}
              </Listbox.Option>
            ))}
          </Listbox.Options>
        </Transition>
      </div>
    </Listbox>
  )
}
